/**
 * moderation.ts — M156, the PURE half of REPORT + BLOCK (App Store Guideline
 * 1.2). The game shows names other players chose (usernames, club names);
 * Apple requires a way to report them, a way to block a player, and a prompt
 * response. The server half is supabase/migrations/m156_player_reports.sql +
 * the report-player edge function; the sheets are ui/social/playerSheet.ts.
 *
 * Two jobs here:
 *  1. The report reasons the sheet offers (ids = the server's REPORT_REASONS
 *     keys — tests/config/report.test.ts keeps the two lists in step).
 *  2. The BLOCKED-NAMES list for this session: filled from the account
 *     (my_blocks) on sign-in, updated the moment the player blocks or
 *     unblocks, cleared on sign-out / account switch (it is account state —
 *     it must never leak to the next person who signs in on the device). Any
 *     name on it shows as BLOCKED RACER wherever online names render.
 *
 * Names are matched case-insensitively (usernames are unique that way).
 */

export type ReportReasonId = 'offensive_name' | 'harassment' | 'cheating' | 'impersonation' | 'other';
export type ReportContextId =
  | 'lobby'
  | 'results'
  | 'tour'
  | 'club'
  | 'friends'
  | 'request'
  | 'invite'
  | 'other';

export interface ReportReasonOption {
  id: ReportReasonId;
  label: string;
}

/** What a player can be reported for, in sheet order. */
export const PLAYER_REPORT_REASONS: readonly ReportReasonOption[] = [
  { id: 'offensive_name', label: 'OFFENSIVE OR INAPPROPRIATE NAME' },
  { id: 'harassment', label: 'HARASSMENT OR BULLYING' },
  { id: 'cheating', label: 'CHEATING' },
  { id: 'impersonation', label: 'PRETENDING TO BE SOMEONE ELSE' },
  { id: 'other', label: 'SOMETHING ELSE' },
];

/** What a club can be reported for (its name is the only thing it shows). */
export const CLUB_REPORT_REASONS: readonly ReportReasonOption[] = [
  { id: 'offensive_name', label: 'OFFENSIVE OR INAPPROPRIATE CLUB NAME' },
  { id: 'impersonation', label: 'PRETENDING TO BE AN OFFICIAL CLUB' },
  { id: 'other', label: 'SOMETHING ELSE' },
];

/** The longest note a reporter can add (the server caps at 500). */
export const REPORT_NOTE_MAX = 300;

/** What a blocked player's name reads as, everywhere online. */
export const BLOCKED_LABEL = 'Blocked racer';

/** One line for the result of a send, in the game's voice. */
export function reportResultMessage(status: string): string {
  switch (status) {
    case 'ok':
      return 'REPORT SENT. Thank you — we review every report within 24 hours.';
    case 'slow_down':
      return "You've sent a lot of reports. Please wait a few minutes and try again.";
    case 'sign_in':
      return 'Sign in with your Racer ID to send a report.';
    default:
      return "Couldn't send the report — check your connection and try again.";
  }
}

/** One line for the result of a block / unblock. */
export function blockResultMessage(status: string, name: string, blocked: boolean): string {
  const who = name.toUpperCase();
  if (status === 'ok') {
    return blocked
      ? `${who} is blocked. You won't see their name online, and they can't send you friend requests or club invites.`
      : `${who} is unblocked.`;
  }
  if (status === 'rate') return 'Too many changes in a row — try again in a little while.';
  if (status === 'self') return "That's you!";
  return "Couldn't reach the server — check your connection and try again.";
}

// ---- the session's blocked list --------------------------------------------------------------

export interface BlockedPlayer {
  /** Null for a name blocked in a race that matched no account (a pool
   *  handle). The server keeps nothing for those; this session masks it. */
  userId: string | null;
  name: string;
}

const key = (name: string): string => name.trim().toLowerCase();

let blocked: BlockedPlayer[] = [];
let blockedKeys = new Set<string>();
const listeners = new Set<() => void>();

function changed(): void {
  blockedKeys = new Set(blocked.map((b) => key(b.name)).filter((k) => k !== ''));
  for (const fn of listeners) {
    try {
      fn();
    } catch (err) {
      console.warn('[moderation] listener failed', err);
    }
  }
}

/** Replace the list (the account's my_blocks answer). Name-only entries made
 *  this session survive a refresh — the server never stored them. */
export function setBlockedList(list: readonly BlockedPlayer[]): void {
  const fromServer = list.filter((b) => b.name.trim() !== '');
  const serverKeys = new Set(fromServer.map((b) => key(b.name)));
  const nameOnly = blocked.filter((b) => b.userId === null && !serverKeys.has(key(b.name)));
  blocked = [...fromServer, ...nameOnly];
  changed();
}

/** Forget everything (sign-out, a different account signing in). */
export function clearBlockedList(): void {
  if (blocked.length === 0) return;
  blocked = [];
  changed();
}

export function blockedList(): readonly BlockedPlayer[] {
  return blocked;
}

export function isBlockedName(name: string): boolean {
  const k = key(name);
  return k !== '' && blockedKeys.has(k);
}

/** The player just blocked this name (shown at once; my_blocks confirms). */
export function noteBlocked(name: string, userId: string | null = null): void {
  if (key(name) === '' || isBlockedName(name)) return;
  blocked = [{ userId, name: name.trim() }, ...blocked];
  changed();
}

/** The player just unblocked this name. */
export function noteUnblocked(name: string): void {
  const k = key(name);
  const next = blocked.filter((b) => key(b.name) !== k);
  if (next.length === blocked.length) return;
  blocked = next;
  changed();
}

/** Called after any change to the list (screens repaint their names). */
export function onBlockedChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** How an online racer's name reads: blocked names never show. */
export function displayRacerName(name: string): string {
  return isBlockedName(name) ? BLOCKED_LABEL : name;
}

// ---- ADMIN DASHBOARD → REPORTS (owner only; admin_reports / admin_moderate) ---------------------

export type ModerationAction = 'dismiss' | 'rename' | 'suspend' | 'unsuspend' | 'rename_club';

/** One report as the owner's Reports panel lists it. */
export interface AdminReport {
  id: string;
  createdAt: string;
  status: 'open' | 'actioned' | 'dismissed';
  action: string | null;
  kind: 'player' | 'club';
  /** The name exactly as the reporter saw it. */
  name: string;
  /** The account that name matched when reported, and what it is called NOW. */
  userId: string | null;
  userNow: string | null;
  userSuspended: boolean;
  clubId: string | null;
  clubNow: string | null;
  reason: string;
  context: string;
  room: string | null;
  details: string | null;
  reporter: string | null;
  /** Open reports on the same name (this one included). */
  openOnTarget: number;
  emailed: boolean;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

/** Parse admin_reports() — defensive: anything malformed is skipped. */
export function parseAdminReports(data: unknown): AdminReport[] {
  if (!Array.isArray(data)) return [];
  const out: AdminReport[] = [];
  for (const raw of data) {
    if (!raw || typeof raw !== 'object') continue;
    const r = raw as Record<string, unknown>;
    const id = str(r.id);
    const name = str(r.name);
    if (!id || !name) continue;
    const status = r.status === 'actioned' || r.status === 'dismissed' ? r.status : 'open';
    out.push({
      id,
      createdAt: str(r.created_at) ?? '',
      status,
      action: str(r.action),
      kind: r.kind === 'club' ? 'club' : 'player',
      name,
      userId: str(r.user_id),
      userNow: str(r.user_now),
      userSuspended: r.user_suspended === true,
      clubId: str(r.club_id),
      clubNow: str(r.club_now),
      reason: str(r.reason) ?? 'other',
      context: str(r.context) ?? 'other',
      room: str(r.room),
      details: str(r.details),
      reporter: str(r.reporter),
      openOnTarget: typeof r.open_on_target === 'number' ? r.open_on_target : 1,
      emailed: r.emailed === true,
    });
  }
  return out;
}

/** The reason as the Reports panel shows it. */
export function reasonLabel(id: string): string {
  return (
    [...PLAYER_REPORT_REASONS, ...CLUB_REPORT_REASONS].find((r) => r.id === id)?.label ??
    'SOMETHING ELSE'
  );
}

/** "3 MIN AGO", "5 H AGO", "2 DAYS AGO" — the 24-hour clock is what matters. */
export function ageLabel(iso: string, now = Date.now()): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const min = Math.max(0, Math.round((now - t) / 60000));
  if (min < 60) return `${min} MIN AGO`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} H AGO`;
  return `${Math.round(h / 24)} DAYS AGO`;
}

/** Where a report was made, as the Reports panel shows it. */
export function contextLabel(id: string): string {
  const labels: Record<string, string> = {
    lobby: 'RACE LOBBY',
    results: 'RACE RESULTS',
    tour: 'TOUR STANDINGS',
    club: 'RACER CLUB',
    friends: 'FRIENDS',
    request: 'FRIEND REQUEST',
    invite: 'CLUB INVITE',
  };
  return labels[id] ?? 'ELSEWHERE';
}
